import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { breakdownAnglesApi } from '../services/api';
import { BreakdownAngle, CreateBreakdownAngleData } from '../types';

const BreakdownAnglesManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingAngle, setEditingAngle] = useState<BreakdownAngle | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data: anglesData, isLoading } = useQuery({
    queryKey: ['breakdown-angles', { page, search }],
    queryFn: () => breakdownAnglesApi.getAll({ page, limit: 10, search: search || undefined }),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<CreateBreakdownAngleData>();

  const createMutation = useMutation({
    mutationFn: breakdownAnglesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdown-angles'] });
      reset();
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateBreakdownAngleData }) =>
      breakdownAnglesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdown-angles'] });
      reset();
      setEditingAngle(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: breakdownAnglesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['breakdown-angles'] });
    },
  });

  const onSubmit = (data: CreateBreakdownAngleData) => {
    if (editingAngle) {
      updateMutation.mutate({ id: editingAngle.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (angle: BreakdownAngle) => {
    setEditingAngle(angle);
    reset({ name: angle.name, description: angle.description || '' });
    setShowForm(true);
  };

  const handleDelete = (angle: BreakdownAngle) => {
    if (window.confirm(`Are you sure you want to delete "${angle.name}"?`)) {
      deleteMutation.mutate(angle.id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAngle(null);
    reset();
  };

  return (
    <div className="breakdown-angles-management">
      <div className="page-header">
        <h1>Breakdown Angles Management</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} disabled={showForm}>
          Add New Angle
        </button>
      </div>

      {showForm && (
        <div className="form-container">
          <h2>{editingAngle ? 'Edit Angle' : 'Create New Angle'}</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="admin-form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                {...register('name', { required: 'Name is required' })}
                type="text"
                id="name"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name.message}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea {...register('description')} id="description" rows={3} />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingAngle ? 'Update' : 'Create'} Angle
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-controls">
        <input
          type="text"
          placeholder="Search angles..."
          aria-label="Search angles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      {isLoading ? (
        <div className="loading">Loading angles...</div>
      ) : anglesData && anglesData.data.length > 0 ? (
        <>
          <div className="table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Breakdowns</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {anglesData.data.map((angle) => (
                  <tr key={angle.id}>
                    <td><strong>{angle.name}</strong></td>
                    <td>{angle.description || '-'}</td>
                    <td>{angle.breakdownsCount || 0}</td>
                    <td>{new Date(angle.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn btn-small btn-secondary" onClick={() => handleEdit(angle)}>Edit</button>
                        <button className="btn btn-small btn-danger" onClick={() => handleDelete(angle)} disabled={deleteMutation.isPending}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {anglesData.pagination.pages > 1 && (
            <div className="pagination">
              {Array.from({ length: anglesData.pagination.pages }, (_, i) => i + 1).map((pageNum) => (
                <button key={pageNum} className={`btn btn-small ${pageNum === page ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPage(pageNum)}>
                  {pageNum}
                </button>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="empty-state">
          <p>No breakdown angles found. Create your first angle!</p>
        </div>
      )}
    </div>
  );
};

export default BreakdownAnglesManagement;
